import { Map } from 'immutable';
import {
    SYSTEM_GET_SETTING,
    SYSTEM_SET_SETTING,
    SYSTEM_GET_WALLET,
    SYSTEM_SET_WALLET,
    SYSTEM_GET_MENU,
    SYSTEM_SET_MENU,
    SYSTEM_GET_ROLEMAP,
    SYSTEM_SET_MENU_KEYS,
    SYSTEM_GET_THEME,
} from '../actions/system';
import { APP_RESET } from '../actions/reset';
import { createActionHandler, createAsyncActionHandler } from './creator';

const initial = Map({
    isGettingSetting: false,
    isSettingSetting: false,
    isGettingWallet: false,
    isSettingWallet: false,
    isGettingMenu: false,
    setting: Map({}),
    wallet: [],
    menu: null,
    menuKeys: [],
    // Theme images keyed by mode ('light' / 'dark'), each value is the full theme bundle
    // returned by /v2/platform/setting/theme: {favicon, mainLogo, squareLogo, clientLoginBg}.
    // 主题图片按 mode 拆分存放（'light' / 'dark'），值是 /v2/platform/setting/theme 的完整响应。
    theme: Map({ light: null, dark: null }),
    isGettingTheme: false,
});

const handlers = {
    ...createActionHandler(SYSTEM_SET_MENU_KEYS, (state: any, action: Action.Sync) =>
        state.set('menuKeys', action.payload.menuKeys),
    ),

    ...createActionHandler(APP_RESET, (state: any) => initial.set('setting', state.get('setting'))),

    // 获取系统设置
    ...createAsyncActionHandler(SYSTEM_GET_SETTING, {
        init: (state: any) =>
            state.merge({
                isGettingSetting: true,
            }),
        succeeded: (state: any, action: Action.Sync) =>
            state.merge({
                isGettingSetting: false,
                setting: state.get('setting').merge(action.payload),
            }),
        failed: (state: any, action: Action.Sync) =>
            state.merge({
                isGettingSetting: false,
                setting: state.get('setting').merge(action.payload),
            }),
    }),

    // 提交系统设置
    ...createAsyncActionHandler(SYSTEM_SET_SETTING, {
        init: (state: any) =>
            state.merge({
                isSettingSetting: true,
            }),
        succeeded: (state: any, action: Action.Sync) =>
            state.merge({
                isSettingSetting: false,
                setting: state.get('setting').merge(action.payload),
            }),
        failed: (state: any, action: Action.Sync) =>
            state.merge({
                isSettingSetting: false,
            }),
    }),

    // 获取钱包设置
    ...createAsyncActionHandler(SYSTEM_GET_WALLET, {
        init: (state: any) =>
            state.merge({
                isGettingWallet: true,
            }),
        succeeded: (state: any, action: Action.Sync) =>
            state.merge({
                isGettingWallet: false,
                wallet: action.payload,
            }),
        failed: (state: any, action: Action.Sync) =>
            state.merge({
                isGettingWallet: false,
            }),
    }),

    // 保存钱包设置
    ...createAsyncActionHandler(SYSTEM_SET_WALLET, {
        init: (state: any) =>
            state.merge({
                isSettingWallet: true,
            }),
        succeeded: (state: any, action: Action.Sync) =>
            state.merge({
                isSettingWallet: false,
                wallet: action.payload,
            }),
        failed: (state: any, action: Action.Sync) =>
            state.merge({
                isSettingWallet: false,
            }),
    }),

    // 获取菜单
    ...createAsyncActionHandler(SYSTEM_GET_MENU, {
        init: (state: any) =>
            state.merge({
                isGettingMenu: true,
            }),
        succeeded: (state: any, action: Action.Sync) =>
            state.merge({
                isGettingMenu: false,
                menu: action.payload || state.get('menu'),
            }),
        failed: (state: any, action: Action.Sync) =>
            state.merge({
                isGettingMenu: false,
            }),
    }),

    // 设置菜单
    ...createAsyncActionHandler(SYSTEM_SET_MENU, {
        init: (state: any) =>
            state.merge({
                isSettingMenu: true,
            }),
        succeeded: (state: any, action: Action.Sync) =>
            state.merge({
                isSettingMenu: false,
            }),
        failed: (state: any, action: Action.Sync) =>
            state.merge({
                isSettingMenu: false,
            }),
    }),

    // 获取角色ID对应描述
    ...createAsyncActionHandler(SYSTEM_GET_ROLEMAP, {
        init: (state: any) =>
            state.merge({
                isGettingRoleMap: true,
            }),
        succeeded: (state: any, action: Action.Sync) =>
            state.merge({
                isGettingRoleMap: false,
                roleMap: state.get('roleMap').merge(action.payload),
            }),
        failed: (state: any, action: Action.Sync) =>
            state.merge({
                isGettingRoleMap: false,
            }),
    }),

    // 获取主题图片（favicon/mainLogo/squareLogo/clientLoginBg）
    // Payload shape: { mode: 'light' | 'dark', data: { favicon, mainLogo, squareLogo, clientLoginBg } }.
    // Payloads are keyed by mode so the header (and any other consumer) can resolve
    // independently of which call arrived first.
    // 主题图片按 mode 分桶存储，header 及其他消费方可独立读取，互不依赖调用顺序。
    ...createAsyncActionHandler(SYSTEM_GET_THEME, {
        init: (state: any) =>
            state.merge({
                isGettingTheme: true,
            }),
        succeeded: (state: any, action: Action.Sync) => {
            const { mode, data } = action.payload || {};
            return state.merge({
                isGettingTheme: false,
                theme: state.get('theme').set(mode || 'light', data),
            });
        },
        failed: (state: any) =>
            state.merge({
                isGettingTheme: false,
            }),
    }),
};

export const system = initial;
export default (state = system, action: any = {}): any => {
    const fn = handlers[action.type];
    return fn ? fn(state, action) : state;
};
